import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

/**
 * 공통 Alert Dialog 컴포넌트
 * JavaScript alert()를 대체하는 MUI 스타일 모달
 */
const AlertDialog = ({
  open,
  onClose,
  title,
  message,
  type = 'info', // 'success', 'error', 'warning', 'info'
  confirmText = '확인',
  showCloseButton = true
}) => {
  // 타입별 아이콘과 색상 설정
  const getTypeConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50' }} />,
          titleColor: '#4caf50'
        };
      case 'error':
        return {
          icon: <ErrorIcon sx={{ fontSize: 48, color: '#f44336' }} />,
          titleColor: '#f44336'
        };
      case 'warning':
        return {
          icon: <WarningIcon sx={{ fontSize: 48, color: '#ff9800' }} />,
          titleColor: '#ff9800'
        };
      case 'info':
      default:
        return {
          icon: <InfoIcon sx={{ fontSize: 48, color: '#2196f3' }} />,
          titleColor: '#2196f3'
        };
    }
  };

  const typeConfig = getTypeConfig();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 0 20px rgba(0,0,0,0.1)'
        }
      }}
    >
      {/* 닫기 버튼 */}
      {showCloseButton && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: '#666',
              '&:hover': {
                color: '#1976d2',
                bgcolor: 'rgba(25, 118, 210, 0.08)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <DialogContent sx={{ textAlign: 'center', pt: showCloseButton ? 0 : 3, pb: 2 }}>
        {/* 아이콘 */}
        <Box sx={{ mb: 2 }}>
          {typeConfig.icon}
        </Box>

        {/* 제목 */}
        {title && (
          <DialogTitle
            sx={{
              p: 0,
              mb: 2,
              fontSize: '1.25rem',
              fontWeight: 600,
              color: typeConfig.titleColor
            }}
          >
            {title}
          </DialogTitle>
        )}

        {/* 메시지 */}
        <Typography
          variant="body1"
          sx={{
            color: '#555',
            lineHeight: 1.6,
            whiteSpace: 'pre-line' // \n을 줄바꿈으로 처리
          }}
        >
          {message}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', px: 3, pb: 3 }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            bgcolor: '#1976d2',
            '&:hover': {
              bgcolor: '#1565c0'
            }
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertDialog; 